import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Loading, Trans } from '@/components'
import { formatCompactNumber } from '@/utils/formatter'

import { fetchRouteMonitorSummary } from '../api'
import { ModelRoute } from '../types'
import { SectionLabel } from './SectionLabel'

interface RouteMonitorProps {
  route: ModelRoute
}

function CompactValue({ value }: { value: number }) {
  const formatted = formatCompactNumber(value)
  const match = formatted.match(/^([\d.,]+)([A-Za-z]*)$/)

  if (!match) {
    return <span className="t-sb text-primary">{formatted}</span>
  }

  return (
    <span>
      <span className="t-sb text-primary">{match[1]}</span>
      {match[2] && <span className="text-secondary"> {match[2]}</span>}
    </span>
  )
}

export function RouteMonitor({ route }: RouteMonitorProps) {
  const { t } = useTranslation('modelRoute')
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({
    queryKey: ['routeMonitorSummary', route.resID],
    queryFn: () => fetchRouteMonitorSummary(route.resID),
  })

  if (isLoading || !data) {
    return (
      <div className="bg-panel-gray-100 rounded-lg flex items-center justify-center p-8">
        <Loading />
      </div>
    )
  }

  return (
    <div className="bg-panel-gray-100 rounded-lg p-4 flex flex-col gap-4 t-sm text-tertiary">
      <div className="grid grid-cols-4 gap-4">
        <div className="space-y-1">
          <div className="t-xs text-secondary">
            <Trans ns="modelRoute" i18nKey="detail.monitor.totalInputTokens">
              Total Input Tokens
            </Trans>
          </div>
          <CompactValue value={data.totalInputTokens} />
        </div>
        <div className="space-y-1">
          <div className="t-xs text-secondary">
            <Trans ns="modelRoute" i18nKey="detail.monitor.totalOutputTokens">
              Total Output Tokens
            </Trans>
          </div>
          <CompactValue value={data.totalOutputTokens} />
        </div>
        <div className="space-y-1">
          <div className="t-xs text-secondary">
            <Trans ns="modelRoute" i18nKey="detail.monitor.totalCachedTokens">
              Total Cached Tokens
            </Trans>
          </div>
          <CompactValue value={data.totalCachedTokens} />
        </div>
      </div>

      {data.topModels.map((item, index) => (
        <div key={item.model} className="flex flex-col gap-4">
          <SectionLabel>
            {t('detail.monitor.modelsByUsage', '#{{rank}} Models by Usage', {
              rank: index + 1,
            })}
          </SectionLabel>
          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-1">
              <div className="t-xs text-secondary">
                <Trans ns="modelRoute" i18nKey="detail.monitor.headerModel">
                  Model
                </Trans>
              </div>
              <div className="t-sb text-primary">{item.model}</div>
            </div>
            <div className="space-y-1">
              <div className="t-xs text-secondary">
                <Trans ns="modelRoute" i18nKey="detail.monitor.headerInputTokens">
                  Input Tokens
                </Trans>
              </div>
              <CompactValue value={item.inputTokens} />
            </div>
            <div className="space-y-1">
              <div className="t-xs text-secondary">
                <Trans ns="modelRoute" i18nKey="detail.monitor.headerOutputTokens">
                  Output Tokens
                </Trans>
              </div>
              <CompactValue value={item.outputTokens} />
            </div>
            <div className="space-y-1">
              <div className="t-xs text-secondary">
                <Trans ns="modelRoute" i18nKey="detail.monitor.headerCachedTokens">
                  Cached Tokens
                </Trans>
              </div>
              <CompactValue value={item.cachedTokens} />
            </div>
          </div>
        </div>
      ))}

      <div className="h-px bg-neutral-200" />

      <div
        className="t-sb text-highlight cursor-pointer w-fit"
        onClick={() => navigate(`/model-route/${route.resID}/monitor`)}
      >
        <Trans ns="modelRoute" i18nKey="detail.viewDetail">
          View Detail
        </Trans>
      </div>
    </div>
  )
}
